import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { usePlaylistStore } from '@/store/playlist/store';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Copy, ChevronLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePlaylistAutosave } from '@/hooks/usePlaylistAutosave';
import { PlaylistUnifiedEditor } from '@/components/playlist/tabs/PlaylistUnifiedEditor';
import { PlaylistVideosTab } from '@/components/playlist/tabs/PlaylistVideosTab';
import { compressImage, formatBytes, getBase64Size } from '@/utils/imageCompression';

import { Playlist } from '@/store/playlist/types';

export default function PlaylistEdit() {
  useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { getPlaylistById, updatePlaylist } = usePlaylistStore();

  const playlist = id ? getPlaylistById(id) : null;

  // Early return se playlist não existe
  if (!playlist) {
    return null;
  }

  const [formData, setFormData] = useState<Partial<Playlist>>({
    name: '',
    description: '',
    thumbnailUrl: '',
    videos: [],
    theme: 'blue',
    template: 'clean',
    templateSettings: undefined,
    appearanceSettings: undefined,
    publicTitle: undefined,
    headerTitle: undefined,
    customLogo: undefined,
    favicon: undefined,
    creatorName: undefined,
    externalButton: undefined,
    footerSettings: undefined,
    access: 'public',
    showProgress: true,
    enableFunnel: false,
    darkMembersSettings: undefined,
    primeVideoSettings: undefined,
  });

  useEffect(() => {
    setFormData({
      name: playlist.name || '',
      description: playlist.description || '',
      thumbnailUrl: playlist.thumbnailUrl || '',
      videos: playlist.videos || [],
      theme: playlist.theme || 'blue',
      template: playlist.template || 'clean',
      templateSettings: playlist.templateSettings,
      appearanceSettings: playlist.appearanceSettings,
      publicTitle: playlist.publicTitle,
      headerTitle: playlist.headerTitle,
      customLogo: playlist.customLogo,
      favicon: playlist.favicon,
      creatorName: playlist.creatorName,
      externalButton: playlist.externalButton,
      footerSettings: playlist.footerSettings,
      access: playlist.access || 'public',
      showProgress: playlist.showProgress ?? true,
      enableFunnel: playlist.enableFunnel ?? false,
      darkMembersSettings: playlist.darkMembersSettings,
      primeVideoSettings: playlist.primeVideoSettings,
    });
  }, [playlist]);

  const updateField = <K extends keyof Playlist>(
    key: K,
    value: Playlist[K]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Auto-save hook
  const { isSaving, lastSaved, hasUnsavedChanges } = usePlaylistAutosave(formData, {
    playlistId: id!,
    debounceMs: 1500,
  });

  const handleCopyCode = () => {
    if (!playlist?.publicHash) return;
    
    const embedCode = `<script src="https://hostvsl.com/embed/${playlist.publicHash}"></script>`;
    navigator.clipboard.writeText(embedCode);
    
    toast({
      title: translateInterfaceText("Código copiado!"),
      description: translateInterfaceText("O código de incorporação foi copiado para a área de transferência."),
    });
  };

  const handlePreview = () => {
    if (!playlist?.publicHash) return;
    window.open(`/playlist/p/${playlist.publicHash}`, '_blank');
  };

  const handleLogoChange = async (file: File) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const original = reader.result as string;
      const originalSize = getBase64Size(original);
      
      try {
        // Comprimir logo para max 400px com qualidade 0.7
        const compressed = await compressImage(original, 400, 0.7);
        const compressedSize = getBase64Size(compressed);
        
        console.log(`Logo: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);
        
        updateField('customLogo', compressed);
        
        toast({
          title: translateInterfaceText("Logo otimizado"),
          description: translateInterfaceText("Reduzido de {value0} para {value1}", { value0: (formatBytes(originalSize)), value1: (formatBytes(compressedSize)) }),
          duration: 2000,
        });
      } catch (error) {
        console.error('Erro ao comprimir logo:', error);
        updateField('customLogo', original); // Fallback para original se falhar
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFaviconChange = async (file: File) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const original = reader.result as string;
      const originalSize = getBase64Size(original);
      
      try {
        // Comprimir favicon para max 200px com qualidade 0.8 (precisa mais qualidade)
        const compressed = await compressImage(original, 200, 0.8);
        const compressedSize = getBase64Size(compressed);
        
        console.log(`Favicon: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);
        
        updateField('favicon', compressed);
        
        toast({
          title: translateInterfaceText("Favicon otimizado"),
          description: translateInterfaceText("Reduzido de {value0} para {value1}", { value0: (formatBytes(originalSize)), value1: (formatBytes(compressedSize)) }),
          duration: 2000,
        });
      } catch (error) {
        console.error('Erro ao comprimir favicon:', error);
        updateField('favicon', original); // Fallback para original se falhar
      }
    };
    reader.readAsDataURL(file);
  };

  // Verifica se a playlist tem pelo menos 1 vídeo
  // Para Dark Members, verifica dentro dos módulos e bônus
  const isPlaylistReady = useMemo(() => {
    // Para templates normais, verificar array videos
    const hasVideosInArray = (formData.videos?.length ?? 0) > 0;
    
    // Para Dark Members, verificar dentro dos módulos e bônus
    if (formData.template === 'dark-members' && formData.darkMembersSettings) {
      const moduleLessons = formData.darkMembersSettings.modules?.reduce(
        (acc, module) => acc + (module.lessons?.length ?? 0), 0
      ) ?? 0;
      
      const bonusLessons = formData.darkMembersSettings.bonuses?.reduce(
        (acc, bonus) => acc + (bonus.lessons?.length ?? 0), 0
      ) ?? 0;
      
      return (moduleLessons + bonusLessons) > 0;
    }
    
    return hasVideosInArray;
  }, [formData.videos, formData.template, formData.darkMembersSettings]);

  const lastEditTime = new Date(playlist.updatedAt).toLocaleString(getInterfaceLanguage(), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Layout>
      <div className="min-h-screen bg-background">
          {/* Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                {/* Título e Subtítulo */}
                <div className="text-left">
                  <h1 className="text-2xl font-bold text-foreground">{translateInterfaceText("Playlists")}</h1>
                  <button 
                    onClick={() => navigate('/playlists')}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1 hover:text-[#2D6CF6] transition-colors group"
                  >
                    <ChevronLeft className="w-4 h-4 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                    <span>{formData.name || translateInterfaceText("Nova Playlist")}</span>
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  {/* Indicador de Status de Auto-Save */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {isSaving ? (
                      <>
                        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                        <span>{translateInterfaceText("Salvando...")}</span>
                      </>
                    ) : hasUnsavedChanges ? (
                      <>
                        <div className="w-2 h-2 bg-amber-500 rounded-full" />
                        <span>{translateInterfaceText("Alterações pendentes")}</span>
                      </>
                    ) : lastSaved ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span>{translateInterfaceText("Salvo")}</span>
                      </>
                    ) : null}
                  </div>

                  {/* Botão Visualizar */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={!isPlaylistReady ? 'cursor-not-allowed' : ''}>
                          <Button 
                            onClick={handlePreview} 
                            variant="outline" 
                            className="gap-2"
                            disabled={!isPlaylistReady}
                          >
                            <Eye className="w-4 h-4" />
                            {translateInterfaceText("Visualizar")}</Button>
                        </span>
                      </TooltipTrigger>
                      {!isPlaylistReady && (
                        <TooltipContent>
                          <p>{translateInterfaceText("Adicione pelo menos 1 vídeo para ativar")}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  
                  {/* Botão Copiar Código */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={!isPlaylistReady ? 'cursor-not-allowed' : ''}>
                          <Button 
                            onClick={handleCopyCode} 
                            className={cn(
                              "gap-2",
                              !isPlaylistReady && "bg-[#93B5FF] hover:bg-[#93B5FF] cursor-not-allowed pointer-events-none"
                            )}
                            disabled={!isPlaylistReady}
                          >
                            <Copy className="w-4 h-4" />
                            {translateInterfaceText("Copiar Código")}</Button>
                        </span>
                      </TooltipTrigger>
                      {!isPlaylistReady && (
                        <TooltipContent>
                          <p>{translateInterfaceText("Adicione pelo menos 1 vídeo para ativar")}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Tabs defaultValue="configuracoes" className="space-y-6">
            <TabsList className="flex gap-2 bg-transparent p-0">
              <TabsTrigger value="configuracoes">{translateInterfaceText("Configurações")}</TabsTrigger>
              <TabsTrigger value="videos">{translateInterfaceText("Vídeos")}</TabsTrigger>
            </TabsList>

            <TabsContent value="configuracoes">
              <PlaylistUnifiedEditor
                playlist={formData as Playlist}
                onUpdate={updateField}
              />
            </TabsContent>

            <TabsContent value="videos">
              <PlaylistVideosTab
                videos={formData.videos || []}
                onVideosChange={(videos) => updateField('videos', videos)}
                template={formData.template}
                darkMembersSettings={formData.darkMembersSettings}
                onDarkMembersSettingsChange={(settings) => updateField('darkMembersSettings', settings)}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
